import { useEffect, useState } from 'react';
import { Box, Typography, Loader, SearchBar } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useFeatureTravellerStore } from 'src/stores';

export default function Search({ searchValue, setSearchValue, handleExitSearch, places = [], onFeatureClick, onTravellerClick }) {
    const [debouncedSearch, setDebouncedSearch] = useState(searchValue);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const isMobile = useIsMobile();
    const travellers = useFeatureTravellerStore((state) => state.travellers);
    
    // Debounce effect
    useEffect(() => {
        if (searchValue === debouncedSearch) return;

        setIsDebouncing(true);
        const handler = setTimeout(() => {
        setDebouncedSearch(searchValue);
        setIsDebouncing(false);
        }, 300);

        return () => clearTimeout(handler);
    }, [searchValue]);

    const lowerSearch = debouncedSearch.toLowerCase().trim();
    const isSearching = lowerSearch.length > 0;

    const placeResults = places.filter(
        p => p?.properties?.title?.toLowerCase().includes(lowerSearch)
    );

    const userResults = travellers.filter(
        u =>
        u?.name?.toLowerCase().includes(lowerSearch) ||
        u?.bio?.toLowerCase().includes(lowerSearch)
    );

    const hasResults = placeResults.length > 0 || userResults.length > 0;

    return (
        <Box
        className="scrollBox"
        sx={{
            pt: isMobile ? 'var(--safe-area-top)' : 0,
            position: 'relative',
            width: '100%',
            height: '100%',
            pr: 2,
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease-in-out',
            gap: 2
        }}>

            {isMobile && (
                <SearchBar
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    handleExitSearch={handleExitSearch}
                    />
            )}

            <Box sx={{width: '100%', height: '100%', position: 'relative'}}>

                {/* Loading while debouncing */}
                {isDebouncing && <Loader fullscreen={false} />}

                {/* No input yet */}
                {!isDebouncing && !isSearching && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="h6">Start typing to search</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Search for places or travellers.
                    </Typography>
                    </Box>
                )}

                {/* Searching but no results */}
                {!isDebouncing && isSearching && !hasResults && (
                    <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="h6">No results found</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Try a different keyword.
                    </Typography>
                    </Box>
                )}

                {/* Search results */}
                {!isDebouncing && isSearching && hasResults && (
                    <Box>
                    {placeResults.length > 0 && (
                        <>
                        <Typography variant="h5" sx={{ mt: 1 }}>
                            Places
                        </Typography>
                        {placeResults.map(p => (
                            <Box key={p.properties.id} sx={{ mt: 1 }}>
                                <Typography 
                                    variant="subtitle1"
                                    fontWeight={500}
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => onFeatureClick?.(p.properties, p.geometry.coordinates)}>
                                    {p.properties.title}
                                </Typography>
                            </Box>
                        ))}
                        </>
                    )}

                    {userResults.length > 0 && (
                        <>
                        <Typography variant="h5" sx={{ mt: 3 }}>
                            Travellers
                        </Typography>
                        {userResults.map(u => (
                            <Box key={u.id} sx={{ mt: 1 }}>
                                <Typography 
                                    variant="subtitle1" 
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => onTravellerClick?.(u)}>
                                    {u.name}
                                </Typography>
                            </Box>
                        ))}
                        </>
                    )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}
